import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "../../lib/utils";

const NIVEL_META = {
  // Degrau acima da rede: só o Administrador Educandus o vê, e é onde as redes
  // (clientes) são comparadas entre si.
  educandus: { label: "Educandus", color: "var(--color-brand)" },
  rede: { label: "Rede", color: "var(--color-perfil-gestor)" },
  /* Token próprio, e não um slot da paleta de gráficos emprestado (era
     chart-2): tag de nível é identidade de interface, e amarrá-la à paleta
     fazia a trilha mudar de cor a cada ajuste nos gráficos. */
  regiao: { label: "Região", color: "var(--color-perfil-regional)" },
  escola: { label: "Escola", color: "var(--color-perfil-admin)" },
  turma: { label: "Turma", color: "var(--color-perfil-professor)" }
};

/* Nível desconhecido não pode derrubar a trilha inteira.
   Foi o defeito de 04/08/2026: o degrau das redes chegou com `nivel:
   "educandus"`, que não existia neste mapa, e a desestruturação direta
   (`const { label } = NIVEL_META[nivel]`) lançou TypeError ao renderizar. Como o
   breadcrumb mora no cabeçalho, a página inteira ia com ele — e o sintoma na
   tela foi "não consigo clicar no ranking", porque o clique montava a trilha e a
   trilha explodia. Rótulo cru é degradação aceitável; tela branca não é. */
const metaDoNivel = nivel =>
  NIVEL_META[nivel] || { label: nivel || "—", color: "var(--color-ink-faint)" };

/** Trilha de navegação clicável — usada onde um painel troca de conteúdo em
 * lugar de empilhar drawers (explorer do Gestor/Admin: rede›região›escola›
 * turma; drawer de Turma›Aluno). Mora no cabeçalho principal (AppShell), de
 * altura fixa. Nunca quebra linha: uma 2ª linha ali "vaza" visualmente por
 * cima do filtro/tema ao lado.
 * Em telas estreitas (<sm) a trilha inteira não cabe, então colapsa pra só uma
 * seta de voltar (pro nível anterior) + tag/nome do nível atual; a trilha
 * completa (rede › região › escola › turma) só aparece a partir do sm. */
export function Breadcrumb({ items }) {
  const atual = items[items.length - 1];
  const anterior = items.length > 1 ? items[items.length - 2] : undefined;

  return (
    <div className="flex min-w-0 items-center gap-1 text-[13px]">
      {/* <sm: seta pro nível anterior + tag/nome do nível atual, só */}
      <div
        className="flex min-w-0 items-center gap-1 sm:hidden"
        style={{ pointerEvents: "auto" }}
      >
        {anterior && (
          <button
            onClick={anterior.onClick}
            aria-label={`Voltar para ${anterior.label}`}
            className="grid h-7 w-7 flex-none place-items-center rounded-lg text-[var(--color-ink-soft)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]"
          >
            <ChevronLeft size={16} />
          </button>
        )}
        <span className="flex min-w-0 items-center gap-1.5 px-1 font-semibold text-[var(--color-ink)]">
          {atual.nivel && <NivelTag nivel={atual.nivel} />}
          <span className="truncate">{atual.label}</span>
        </span>
      </div>

      {/* ≥sm: trilha completa, sempre em 1 linha só (nunca quebra) */}
      <div
        className="hidden min-w-0 flex-nowrap items-center gap-1 sm:flex"
        // o .hidden do AVA declara pointer-events:none, que os botões da trilha
        // herdariam; inline garante o clique independente do CSS
        style={{ pointerEvents: "auto" }}
      >
        {items.map((item, i) => {
          const ultimo = i === items.length - 1;
          const conteudo = (
            <>
              {item.nivel && <NivelTag nivel={item.nivel} />}
              <span className="truncate">{item.label}</span>
            </>
          );

          /* Quem encolhe primeiro são os níveis JÁ percorridos, não o atual.

             Antes o último item levava `flex-1` (= `flex: 1 1 0%`, base ZERO),
             então era o primeiro a ser espremido: no nível de turma, com quatro
             níveis na trilha, sobrava só a tag e o nome da turma sumia.

             Agora o atual não encolhe (`shrink-0`) e é limitado a metade da
             largura; os anteriores cedem espaço, truncando o nome mas mantendo
             a tag, que já identifica o nível. */
          return (
            <span
              key={i}
              className={cn(
                "flex min-w-0 items-center gap-1.5",
                ultimo ? "shrink-0" : "shrink"
              )}
              // maxWidth inline: o Tailwind v4 não emitiu a utilitária
              // equivalente para porcentagem, e sem o teto o nível atual
              // empurraria os anteriores para fora em nomes longos.
              style={ultimo ? { maxWidth: "50%" } : undefined}
            >
              {i > 0 && (
                <ChevronRight
                  size={14}
                  className="flex-none text-[var(--color-ink-faint)]"
                />
              )}
              {item.onClick ? (
                <button
                  onClick={item.onClick}
                  className="flex min-w-0 items-center gap-1.5 rounded-lg px-1.5 py-0.5 font-medium text-[var(--color-ink-soft)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]"
                >
                  {conteudo}
                </button>
              ) : (
                <span
                  className={cn(
                    "flex min-w-0 items-center gap-1.5 px-1.5 py-0.5",
                    ultimo
                      ? "font-semibold text-[var(--color-ink)]"
                      : "text-[var(--color-ink-soft)]"
                  )}
                >
                  {conteudo}
                </span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function NivelTag({ nivel }) {
  const { label, color } = metaDoNivel(nivel);
  return (
    <span
      // flex-none: a tag identifica o nível e não deve ser espremida nem
      // quebrar linha quando a trilha fica longa — quem cede é o nome ao lado.
      className="flex-none whitespace-nowrap rounded-md px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-wide"
      style={{
        background: `color-mix(in srgb, ${color} 14%, transparent)`,
        color
      }}
    >
      {label}
    </span>
  );
}
